'use client';

import { useState, useEffect, useCallback } from 'react';
import { BackendAuth } from '@/lib/auth-backend';
import { fetchAuthBootstrap } from '@/lib/auth-bootstrap';

/**
 * Hook to fetch and manage organisation authentication settings
 * Used by login page and settings pages to display available auth methods
 */
export function useAuthSettings(organisationId = null) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch auth settings for organisation
   * NOTE: Uses fetch() directly instead of BackendAuth.apiCallJson() because
   * this is a PUBLIC endpoint that should work without authentication.
   * Using BackendAuth would cause redirect loops on the signin page.
   */
  const fetchAuthSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let endpoint;
      if (organisationId) {
        // Fetch for specific organisation (public endpoint)
        endpoint = `/api/auth/settings/organisation/${organisationId}`;
      } else {
        // Domain-basierter Fall: erst der gebündelte auth-bootstrap-Call
        // (geteilt mit SigninClient — ein Roundtrip statt drei). Bei Fehler
        // fällt der Code unten auf den bisherigen Einzel-Endpoint zurück.
        try {
          const bootstrap = await fetchAuthBootstrap();
          if (bootstrap?.auth_settings?.success) {
            setSettings(bootstrap.auth_settings.settings);
            return;
          }
        } catch {
          // Fallback auf /api/auth/settings/status unten
        }
        // Fetch based on current domain/context (public endpoint)
        endpoint = `/api/auth/settings/status`;
      }

      // Use fetch() directly for public endpoints to avoid redirect loops
      // credentials: 'omit' prevents sending cookies (which might contain old/invalid tokens)
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'omit', // CRITICAL: Don't send cookies to avoid token validation issues
      });

      // Handle non-OK responses gracefully (don't throw on 401)
      if (!response.ok) {
        // For public endpoints, just use defaults on error
        console.warn(`Auth settings endpoint returned ${response.status}, using defaults`);
        setSettings(null);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSettings(data.settings);
      } else {
        setError(data.message || 'Failed to fetch auth settings');
      }
    } catch (err) {
      console.error('Error fetching auth settings:', err);
      setError(err.message || 'Failed to fetch auth settings');
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  /**
   * Update auth settings (admin only)
   */
  const updateAuthSettings = async (updates) => {
    setLoading(true);
    setError(null);

    try {
      const currentOrgId = organisationId || settings?.organisation_id;
      if (!currentOrgId) {
        throw new Error('Organisation ID is required');
      }

      const response = await BackendAuth.apiCallJson(
        `/api/auth/settings/organisation/${currentOrgId}`,
        {
          method: 'PUT',
          body: JSON.stringify(updates),
        }
      );

      if (response.success) {
        setSettings(response.settings);
        return { success: true };
      } else {
        setError(response.message || 'Failed to update auth settings');
        return { success: false, error: response.message };
      }
    } catch (err) {
      console.error('Error updating auth settings:', err);
      setError(err.message || 'Failed to update auth settings');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update OAuth credentials (admin only)
   */
  const updateOAuthCredentials = async (provider, clientId, clientSecret, additionalConfig = {}) => {
    setLoading(true);
    setError(null);

    try {
      const currentOrgId = organisationId || settings?.organisation_id;
      if (!currentOrgId) {
        throw new Error('Organisation ID is required');
      }

      const response = await BackendAuth.apiCallJson(
        `/api/auth/settings/organisation/${currentOrgId}/oauth-credentials`,
        {
          method: 'PUT',
          body: JSON.stringify({
            provider,
            client_id: clientId,
            client_secret: clientSecret,
            additional_config: additionalConfig,
          }),
        }
      );

      if (response.success) {
        // Refresh settings to get updated providers list
        await fetchAuthSettings();
        return { success: true };
      } else {
        setError(response.message || 'Failed to update OAuth credentials');
        return { success: false, error: response.message };
      }
    } catch (err) {
      console.error('Error updating OAuth credentials:', err);
      setError(err.message || 'Failed to update OAuth credentials');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete OAuth credentials (admin only)
   */
  const deleteOAuthCredentials = async (provider) => {
    setLoading(true);
    setError(null);

    try {
      const currentOrgId = organisationId || settings?.organisation_id;
      if (!currentOrgId) {
        throw new Error('Organisation ID is required');
      }

      const response = await BackendAuth.apiCallJson(
        `/api/auth/settings/organisation/${currentOrgId}/oauth-credentials/${provider}`,
        {
          method: 'DELETE',
        }
      );

      if (response.success) {
        // Refresh settings to get updated providers list
        await fetchAuthSettings();
        return { success: true };
      } else {
        setError(response.message || 'Failed to delete OAuth credentials');
        return { success: false, error: response.message };
      }
    } catch (err) {
      console.error('Error deleting OAuth credentials:', err);
      setError(err.message || 'Failed to delete OAuth credentials');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Fetch settings on mount
  useEffect(() => {
    fetchAuthSettings();
  }, [fetchAuthSettings]);

  return {
    settings,
    loading,
    error,
    refresh: fetchAuthSettings,
    updateAuthSettings,
    updateOAuthCredentials,
    deleteOAuthCredentials,

    // Convenience helpers
    isPasskeyEnabled: settings?.passkey?.enabled ?? false,
    isPasskeyRequired: settings?.passkey?.required ?? false,
    isMFAEnabled: settings?.mfa?.enabled ?? false,
    isMFARequiredForAdmins: settings?.mfa?.required_for_admins ?? false,
    isMFARequiredForAll: settings?.mfa?.required_for_all ?? false,
    isPasswordEnabled: settings?.password?.enabled ?? true,
    isOAuthEnabled: settings?.oauth?.enabled ?? false,
    enabledOAuthProviders: settings?.oauth?.providers ?? [],
    // ID Austria (E-ID) — separate gate from generic OAuth. Backend reports
    // available=true only when per-org enabled AND operator credentials are
    // configured, so the button never shows when /login/start would 400.
    isIdAustriaAvailable: settings?.id_austria?.available ?? false,
    passwordRequirements: settings?.password ?? {},
    sessionTimeout: settings?.session?.timeout_minutes ?? 480,
  };
}

export default useAuthSettings;
